import { Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import { aboutData } from '@/mocks/about';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mangovideo.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
    {
      '@type': 'Organization',
      name: '芒果视频',
      url: 'https://mangovideo.com/',
      description: '芒果视频提供1080P超清画质在线播放，电影电视剧全集免费高清观看。',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: aboutData.contact.phone,
        contactType: 'customer service',
        email: aboutData.contact.email,
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: aboutData.contact.address,
      },
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <SEOMeta
        title="关于我们 - 芒果视频 | 1080P超清画质在线影视平台"
        description="芒果视频致力于提供1080P超清画质在线影视观看服务，涵盖电影、电视剧、综艺、动漫等海量资源。了解芒果视频的品牌故事、核心优势和联系方式。"
        keywords="芒果视频,芒果视频介绍,关于芒果视频,芒果视频联系方式,高清影视平台"
        canonicalUrl="https://mangovideo.com/about"
        schema={aboutSchema}
      />

      <main className="pt-16">
        <nav className="px-4 md:px-6 py-4 bg-background-100 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-400">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-700 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-5">
                关于芒果视频
              </h1>
              <div className="space-y-4">
                {aboutData.brandIntro.map((text, idx) => (
                  <p key={idx} className="text-foreground-500 text-sm md:text-base leading-relaxed">
                    {text}
                  </p>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-5 md:p-6 text-center">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                核心优势
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                为什么选择芒果视频？
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {aboutData.features.map((feat) => (
                <div key={feat.title} className="bg-background-50 rounded-xl p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-3">
                    <i className={`${feat.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-800 text-sm md:text-base mb-2">{feat.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="reputation" className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                听听用户们怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {aboutData.testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-700 text-xs font-bold">
                      {t.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                有任何问题或建议，欢迎随时与我们联系
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              <div className="bg-background-50 rounded-xl p-5 md:p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100 text-primary-500">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-800 text-sm mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">{aboutData.contact.email}</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-5 md:p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100 text-primary-500">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-800 text-sm mb-1">客服电话</h3>
                  <p className="text-sm text-foreground-500">{aboutData.contact.phone}</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-5 md:p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100 text-primary-500">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-800 text-sm mb-1">微信公众号</h3>
                  <p className="text-sm text-foreground-500">{aboutData.contact.wechat}</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl p-5 md:p-6 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100 text-primary-500">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-800 text-sm mb-1">公司地址</h3>
                  <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}